import { DateTime } from '@ksyos/date-time';
import {
    Card,
    Col,
    FormGroup,
    FormParagraph,
    Link,
    Row,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import IBAN from 'iban';
import { useEffect, useState } from 'react';
import type { IBankAccountDetails } from '@common/interfaces/bankAccountDetails';
import { formatString } from '@common/utils/formatString';
import type { IPayoutRecordsDetailsWithTotals } from '../../../../common/interfaces/payoutDetails';
import { getActivityNameFromPayoutType } from '../../../../common/utils/getActivityNameFromPayoutType';
import { getGlobalize, getSelectedLanguage } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { FormatCurrency } from '../../elements/FormatCurrency';

interface IPayoutRecordDetailOverviewProps {
    amount: number;
    bankAccountDetailsId?: string;
    organization?: string;
    payoutLevel: string;
    payoutLevelEntityId: string;
    payoutReference?: string;
    relationFileId: string;
    relationNumber?: string | null;
    sentBeforeDate?: string;
}

export function PayoutRecordDetailOverview(props: Readonly<IPayoutRecordDetailOverviewProps>) {
    const abortableFetch = useAbortableFetch();

    const [bankAccountDetails, setBankAccountDetails] =
        useState<Pick<IBankAccountDetails, 'subscriber' | 'bankAccountNumber'>>();
    const [payoutRecordsDetailsWithTotals, setPayoutRecordsDetailsWithTotals] =
        useState<IPayoutRecordsDetailsWithTotals>({
            payoutRecordsDetails: [],
            totalAmount: 0,
            totalAmountExclBTW: 0,
            totalBTW: 0,
        });
    const [hasVatRate, setHasVatRate] = useState<boolean>(false);

    const periodLabel = DateTime.parse(props.sentBeforeDate!, 'DD-MM-YYYY').endOf('quarter').format('DD-MM-YYYY');
    const dateOfPaymentLabel = DateTime.parse(props.sentBeforeDate!, 'DD-MM-YYYY')
        .endOf('quarter')
        .add(2, 'months')
        .add(15, 'days')
        .format('DD MMMM YYYY');
    const globalize = getGlobalize();
    const formatter = globalize.currencyFormatter('EUR');

    useEffect(() => {
        loadPayoutsDetails();
        loadBankAccountDetails();
    }, []);

    return (
        <>
            <Card>
                <Card.Header title="Details" />
                <Card.Body>
                    <FormGroup label={getSelectedLanguage().finance.amount}>
                        <FormParagraph>{formatter(props.amount / 100)}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.period}>
                        <Row columnGap={2}>
                            <Col>
                                <FormParagraph>{getSelectedLanguage().common.until}</FormParagraph>
                            </Col>
                            <Col>
                                <FormParagraph>{periodLabel}</FormParagraph>
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().finance.paidDate}>
                        <FormParagraph>
                            {formatString(getSelectedLanguage().common.around, [dateOfPaymentLabel])}
                        </FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().finance.paymentReference}>
                        <FormParagraph>{props.payoutReference}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().finance.specification}>
                        <Link
                            iconLeft="file"
                            onClick={() => window.open(`/api/relationFiles/download/${props.relationFileId}`)}
                            variant="button"
                        >
                            {`${props.payoutReference}.pdf`}
                        </Link>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().finance.toIban}>
                        <FormParagraph>
                            {bankAccountDetails?.bankAccountNumber &&
                            IBAN.isValid(bankAccountDetails?.bankAccountNumber)
                                ? IBAN.printFormat(bankAccountDetails?.bankAccountNumber)
                                : getSelectedLanguage().common.unknown}
                        </FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().finance.inNameOf}>
                        <FormParagraph>
                            {bankAccountDetails?.subscriber ?? getSelectedLanguage().common.unknown}
                        </FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().organization.organization}>
                        <FormParagraph>{props.organization}</FormParagraph>
                    </FormGroup>
                </Card.Body>
            </Card>
            {payoutRecordsDetailsWithTotals.payoutRecordsDetails.length > 0 && (
                <Card>
                    <Card.Header title={getSelectedLanguage().common.summary} />
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{getSelectedLanguage().finance.activity}</TableHeader>
                                    <TableHeader>{getSelectedLanguage().finance.numberOfPayouts}</TableHeader>
                                    <TableHeader>{getSelectedLanguage().finance.priceAgreement}</TableHeader>
                                    <TableHeader>{getSelectedLanguage().finance.subtotal}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payoutRecordsDetailsWithTotals.payoutRecordsDetails.map((payout, index) => {
                                    return (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {payout.serviceName}-{getActivityNameFromPayoutType(payout.payoutType)}
                                            </TableCell>
                                            <TableCell>
                                                {(payout.isCounterBooking
                                                    ? -payout.count
                                                    : payout.count
                                                ).toLocaleString('nl-NL')}
                                            </TableCell>
                                            <TableCell>
                                                <FormatCurrency value={payout.amount} />
                                            </TableCell>
                                            <TableCell>
                                                <FormatCurrency
                                                    value={
                                                        payout.isCounterBooking
                                                            ? -payout.amount * payout.count
                                                            : payout.amount * payout.count
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {renderTotalAmount()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}
        </>
    );

    function renderTotalAmount() {
        if (hasVatRate) {
            return (
                <>
                    <TableRow>
                        <TableCell>{getSelectedLanguage().finance.totalPayableExclBTW}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            <FormatCurrency value={payoutRecordsDetailsWithTotals.totalAmountExclBTW} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>{getSelectedLanguage().finance.btw}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            <FormatCurrency value={payoutRecordsDetailsWithTotals.totalBTW} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>{getSelectedLanguage().finance.totalPayable}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            <FormatCurrency value={payoutRecordsDetailsWithTotals.totalAmount} />
                        </TableCell>
                    </TableRow>
                </>
            );
        } else {
            return (
                <TableRow>
                    <TableCell>{getSelectedLanguage().finance.totalPayable}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell>
                        <FormatCurrency value={payoutRecordsDetailsWithTotals.totalAmount} />
                    </TableCell>
                </TableRow>
            );
        }
    }

    async function loadPayoutsDetails() {
        const url = `/api/payments/payoutRecordsByRelationFile?relationFileId=${props.relationFileId}&relationNumber=${props.relationNumber}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        const payoutRecordsWithTotals: IPayoutRecordsDetailsWithTotals = result.json.items;

        setPayoutRecordsDetailsWithTotals(payoutRecordsWithTotals);
        setHasVatRate(payoutRecordsWithTotals.payoutRecordsDetails.some((payout) => payout.vatRate > 0));
    }

    async function loadBankAccountDetails() {
        let result;

        if (props.bankAccountDetailsId) {
            result = await abortableFetch.fetchJson(
                `/api/bankAccountDetails?bankAccountDetailsId=${props.bankAccountDetailsId}`,
                'GET'
            );
        } else {
            result = await abortableFetch.fetchJson(
                `/api/bankAccountDetails?entityId=${props.payoutLevelEntityId}&bankAccountEntity=${props.payoutLevel}`,
                'GET'
            );
        }

        setBankAccountDetails(result.json.bankAccountDetails);
    }
}
